import React from "react";

import {
    ActionCell,
    Card,
    Cell,
    HeaderRow,
    IndicatorCell,
    Main,
    Meta,
    Primary,
    Row,
} from "./styles";

/*
* Роль колонки задаёт и её место в строке, и поведение на узком вьюпорте:
* indicator и action остаются по краям, primary и secondary складываются в две строчки.
* Ширины в пикселях — только у secondary и action; primary забирает остаток.
* */
export type DataTableColumnRole = "indicator" | "primary" | "secondary" | "action";

export interface DataTableColumn<T> {
    key: string;
    title?: React.ReactNode;
    role: DataTableColumnRole;
    width?: number;
    align?: "left" | "right";
    render: (row: T) => React.ReactNode;
}

interface DataTableProps<T> {
    columns: DataTableColumn<T>[];
    rows: T[];
    rowKey: (row: T) => React.Key;
    /*
    * Клик по строке. Ставим на строку целиком, а не на ссылку в первой ячейке: попасть в
    * строку проще, чем в текст, а на узком вьюпорте ячейки складываются и цель уменьшается.
    * Ячейка действий из этого исключена — иначе кнопка в строке срабатывала бы вместе с
    * переходом.
    * */
    onRowClick?: (row: T) => void;
    footer?: React.ReactNode;
    className?: string;
}

/*
* Таблица-карточка со строками одинаковой формы. Разметка одна на оба вьюпорта:
* вторичные ячейки на мобильном скрываются, а их содержимое повторяется мета-строкой под
* названием — это дешевле, чем держать два дерева и следить, чтобы они не разъехались.
* */
export function DataTable<T>({
    columns,
    rows,
    rowKey,
    onRowClick,
    footer,
    className,
}: DataTableProps<T>) {
    const indicator = columns.find((column) => column.role === "indicator");
    const primary = columns.find((column) => column.role === "primary");
    const secondaries = columns.filter((column) => column.role === "secondary");
    const action = columns.find((column) => column.role === "action");

    // Пустые ячейки из мета-строки выбрасываем: иначе позиция в категории без названия
    // получает висящий разделитель («· 200 ₽»).
    const renderMeta = (row: T) => {
        const parts = secondaries
            .map((column) => ({ key: column.key, node: column.render(row) }))
            .filter((part) => part.node !== null && part.node !== undefined && part.node !== "");

        return parts.map((part, index) => (
            <React.Fragment key={part.key}>
                {index > 0 && " · "}
                {part.node}
            </React.Fragment>
        ));
    };

    return (
        <Card className={className}>
            <HeaderRow>
                {indicator && <IndicatorCell />}
                <Main>
                    <Primary>{primary?.title}</Primary>
                </Main>
                {secondaries.map((column) => (
                    <Cell key={column.key} $width={column.width} $align={column.align}>
                        {column.title}
                    </Cell>
                ))}
                {action && <ActionCell $width={action.width}>{action.title}</ActionCell>}
            </HeaderRow>

            {rows.map((row) => (
                <Row
                    key={rowKey(row)}
                    $clickable={onRowClick !== undefined}
                    role={onRowClick ? "button" : undefined}
                    tabIndex={onRowClick ? 0 : undefined}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    onKeyDown={
                        onRowClick
                            ? (event) => {
                                  if (event.key !== "Enter" && event.key !== " ") return;
                                  // Пробел на сфокусированной строке иначе прокрутит страницу.
                                  event.preventDefault();
                                  onRowClick(row);
                              }
                            : undefined
                    }
                >
                    {indicator && <IndicatorCell>{indicator.render(row)}</IndicatorCell>}
                    <Main>
                        <Primary>{primary?.render(row)}</Primary>
                        <Meta>{renderMeta(row)}</Meta>
                    </Main>
                    {secondaries.map((column) => (
                        <Cell key={column.key} $width={column.width} $align={column.align}>
                            {column.render(row)}
                        </Cell>
                    ))}
                    {action && (
                        // Действие в строке — не переход по строке: клик до неё не доходит.
                        <ActionCell
                            $width={action.width}
                            onClick={(event) => event.stopPropagation()}
                        >
                            {action.render(row)}
                        </ActionCell>
                    )}
                </Row>
            ))}

            {footer}
        </Card>
    );
}
